import { DragLayerMonitor, useDragLayer } from "react-dnd";
import React, { useCallback, useEffect, useState } from "react";
import "./formBuilderOverlay.scss";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_DRAG_LAYER } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WEBSITE_BUILDER_Z_LAYERS } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderZLayers";
import { isEqual } from "lodash";
import { FORM_BUILDER_FORM_GAP } from "../../model/formBuilderForm";
import { useFormBuilderEditorContext } from "../../formBuilderEditorContext";
import { IFormBuilderRow } from "../../model/formBuilderRow";
import { TFormBuilderItem } from "../../model/item/formBuilderItem";
import { ACCEPTED_FORM_BUILDER_DROP_ITEM } from "../views/components/form/FormBuilderFormView";

interface IFormBuilderOverlayTargetBase {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type TFormBuilderOverlayRowAnchor = "above" | "below";

interface IFormBuilderOverlayRowTarget extends IFormBuilderOverlayTargetBase {
  type: "row";
  anchor: TFormBuilderOverlayRowAnchor;
}

type TFormBuilderOverlayItemAnchor = "left" | "right";

interface IFormBuilderOverlayItemTarget extends IFormBuilderOverlayTargetBase {
  type: "item";
  anchor: TFormBuilderOverlayItemAnchor;
}

export type TFormBuilderOverlayTarget = IFormBuilderOverlayRowTarget | IFormBuilderOverlayItemTarget;

interface IFormBuilderOverlayProps {
  isOver: boolean;
}

interface IFormBuilderOverlayDragLayer {
  x: number;
  y: number;
  width: number;
  height: number;
}

type TFormBuilderOverlayEntityEntry<T> = {
  entity: T;
  element: HTMLDivElement;
}

interface IFormBuilderOverlayClientPosition {
  x: number;
  y: number;
}

const TARGET_THICKNESS = 1;

const DRAG_LAYER_WIDTH = 150;
const DRAG_LAYER_HEIGHT = 50;

const ITEM_SNAP_BIAS = FORM_BUILDER_FORM_GAP / 2;

export function FormBuilderOverlay(props: IFormBuilderOverlayProps) {
  const { isOver } = props;

  const { formBuilderController, formRef, formBuilderEntityDomNodeRefMap } = useFormBuilderEditorContext();

  const { isDragging, clientOffset, item, itemType } = useDragLayer((monitor: DragLayerMonitor) => {
    return {
      isDragging: monitor.isDragging(),
      clientOffset: monitor.getClientOffset(),
      item: monitor.getItem<unknown>(),
      itemType: monitor.getItemType(),
    };
  });

  const [clientPosition, setClientPosition] = useState<IFormBuilderOverlayClientPosition>(null);

  const draggingFormItem =
    isDragging &&
    itemType === ACCEPTED_FORM_BUILDER_DROP_ITEM &&
    item != null &&
    clientOffset != null;

  const draggingFormItemOver = draggingFormItem && isOver;

  const dragLayer: IFormBuilderOverlayDragLayer = draggingFormItem
    ? {
        x: clientOffset.x - (DRAG_LAYER_WIDTH / 2),
        y: clientOffset.y - (DRAG_LAYER_HEIGHT / 2),
        width: DRAG_LAYER_WIDTH,
        height: DRAG_LAYER_HEIGHT,
      }
    : null;
    
  const movingRow = formBuilderController.selectedEntity?.type === "row" && formBuilderController.selectedEntity.editingMode.type === "move";
  const movingItem = formBuilderController.selectedEntity?.type === "item" && formBuilderController.selectedEntity.editingMode.type === "move";
  const movingEntity = movingRow || movingItem;

  const handlePointerMove = useCallback((e: PointerEvent) => {
    setClientPosition({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    const notDragging = !isDragging || clientOffset == null;

    if (notDragging) {
      document.addEventListener("pointermove", handlePointerMove);
    } else {
      setClientPosition({ x: clientOffset.x, y: clientOffset.y });
    }

    return () => {
      if (notDragging) {
        document.removeEventListener("pointermove", handlePointerMove);
      }
    };
  }, [isDragging, clientOffset, handlePointerMove]);

  function getEntityEntries<T extends { key: string }>(entities: T[]) {
    const formBuilderEntityDomNodeRefMapEntries = Array.from(formBuilderEntityDomNodeRefMap.current.entries());

    const entries: TFormBuilderOverlayEntityEntry<T>[] = entities
      .map(entity => {
        const matchingEntry = formBuilderEntityDomNodeRefMapEntries.find(entry => entry[0] === entity.key);

        if (matchingEntry == null) {
          return null;
        }

        return { entity, element: matchingEntry[1] };
      })
      .filter(entry => entry != null);
    
    return entries;
  }

  useEffect(() => {
    if (
      (draggingFormItemOver || movingEntity) &&
      formBuilderController.form != null &&
      clientPosition != null
    ) {
      const formBoundingRect = formRef.current.getBoundingClientRect();

      const rowEntries = getEntityEntries<IFormBuilderRow>(formBuilderController.form.children);

      let target: TFormBuilderOverlayTarget = null;
      let lowestRowDelta = Infinity;

      for (const rowEntry of rowEntries) {
        if (movingRow && rowEntry.entity.key === formBuilderController.selectedEntity.key) {
          continue;
        }

        const row = rowEntry.entity;
        const rowKey = row.key;
        const rowElement = rowEntry.element;

        const rowBoundingRect = rowElement.getBoundingClientRect();
        const rowLeft = rowBoundingRect.left;
        const rowTop = rowBoundingRect.top;
        const rowBottom = rowBoundingRect.bottom;
        const rowWidth = rowBoundingRect.width;
        
        const rowTopDelta = Math.abs(rowTop - clientPosition.y);
        const rowBottomDelta = Math.abs(rowBottom - clientPosition.y);

        const rowMinDelta = Math.min(rowTopDelta, rowBottomDelta);

        if (rowMinDelta < lowestRowDelta) {
          lowestRowDelta = rowMinDelta;

          if (rowMinDelta === rowTopDelta) {
            target = {
              type: "row",
              key: rowKey,
              anchor: "above",
              x: rowLeft - formBoundingRect.left,
              y: rowTop - (FORM_BUILDER_FORM_GAP / 2) - formBoundingRect.top,
              width: rowWidth,
              height: TARGET_THICKNESS,
            };
          } else if (rowMinDelta === rowBottomDelta) {
            target = {
              type: "row",
              key: rowKey,
              anchor: "below",
              x: rowLeft - formBoundingRect.left,
              y: rowBottom + (FORM_BUILDER_FORM_GAP / 2) - formBoundingRect.top,
              width: rowWidth,
              height: TARGET_THICKNESS,
            };
          }

          if (movingRow) {
            continue;
          }

          const itemEntries = getEntityEntries<TFormBuilderItem>(row.children);
          
          let lowestItemDelta = Infinity;

          for (const itemEntry of itemEntries) {
            if (movingItem && itemEntry.entity.key === formBuilderController.selectedEntity.key) {
              continue;
            }

            const item = itemEntry.entity;
            const itemKey = item.key;
            const itemElement = itemEntry.element;

            const itemBoundingRect = itemElement.getBoundingClientRect();
            const itemTop = itemBoundingRect.top;
            const itemLeft = itemBoundingRect.left;
            const itemRight = itemBoundingRect.right;
            const itemHeight = itemBoundingRect.height;

            const itemLeftDelta = Math.abs(itemLeft - clientPosition.x);
            const itemRightDelta = Math.abs(itemRight - clientPosition.x);

            const itemMinDelta = Math.min(itemLeftDelta, itemRightDelta);

            if (
              itemMinDelta < lowestItemDelta &&
              itemMinDelta - ITEM_SNAP_BIAS <= rowMinDelta
            ) {
              lowestItemDelta = itemMinDelta;

              if (itemMinDelta === itemLeftDelta) {
                target = {
                  type: "item",
                  key: itemKey,
                  anchor: "left",
                  x: itemLeft - (FORM_BUILDER_FORM_GAP / 2) - formBoundingRect.left,
                  y: itemTop - formBoundingRect.top,
                  width: TARGET_THICKNESS,
                  height: itemHeight,
                };
              } else if (itemMinDelta === itemRightDelta) {
                target = {
                  type: "item",
                  key: itemKey,
                  anchor: "right",
                  x: itemRight + (FORM_BUILDER_FORM_GAP / 2) - formBoundingRect.left,
                  y: itemTop - formBoundingRect.top,
                  width: TARGET_THICKNESS,
                  height: itemHeight,
                };
              }
            }
          }
        }
      }

      if (!isEqual(formBuilderController.overlayTarget, target)) {
        formBuilderController.updateOverlayTarget({ overlayTarget: target });
      }
    } else {
      formBuilderController.updateOverlayTarget({ overlayTarget: null });
    }
  }, [
    draggingFormItemOver,
    movingEntity,
    movingRow,
    movingItem,
    clientPosition,
    formBuilderController,
  ]);

  return (
    <>
      {dragLayer && (
        <Portal isMounted={true} parentId={WEBSITE_BUILDER_DRAG_LAYER}>
          <div className="form-builder-overlay-drag-layer"
            style={{
              left: dragLayer.x,
              top: dragLayer.y,
              width: dragLayer.width,
              height: dragLayer.height,
              zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
            }}
          />
        </Portal>
      )}

      {formBuilderController.overlayTarget && (
        <div
          className="form-builder-overlay-target"
          style={{
            left: formBuilderController.overlayTarget.x,
            top: formBuilderController.overlayTarget.y,
            width: formBuilderController.overlayTarget.width,
            height: formBuilderController.overlayTarget.height,
          }}
        />
      )}
    </>
  );
}